import { NATIVE_STATE_TYPE } from '@wix/component-protocol/schema';
import type {
  DisplayGroups,
  EditorElement,
} from '@wix/component-protocol/schema';
import { ManifestDisplayFilterResolver } from './ManifestDisplayFilterResolver';
import { ReducibleKey } from '../../builders';

import { ManifestServiceFlags } from '../../ManifestServiceFlags';
import {
  VirtualElement,
  VirtualElementAPIType,
} from '../providers/VirtualElement';
import { ManifestServiceStats } from '../../ManifestServiceStats';
import { AbstractManifestAPI } from '../AbstractManifestAPI';
import { IManifestChangeDetection } from '../../ManifestChangeDetection';
import { ManifestResolverAPI } from '../../handlers';
import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

const VALID_NATIVE_PSEUDO_CLASSES = new Set<string>(
  Object.values(NATIVE_STATE_TYPE).filter(
    (v) => v !== NATIVE_STATE_TYPE.UNKNOWN_NativeStateType,
  ),
);

const isDesignState = (
  state: NonNullable<EditorElement['states']>[string],
): boolean =>
  state.className != null ||
  (state.pseudoClass !== undefined &&
    VALID_NATIVE_PSEUDO_CLASSES.has(state.pseudoClass));

export class ManifestDefinitionsResolver extends AbstractManifestAPI<
  ManifestResolverAPI,
  VirtualElementAPIType.ManifestResolver
> {
  readonly #displayFilterResolver: ManifestDisplayFilterResolver;

  constructor(
    node: VirtualElement<VirtualElementAPIType.ManifestResolver>,
    api: ManifestResolverAPI,
    editorPointerAPI: EditorPointerServiceAPI,
    manifestChangeDetection: IManifestChangeDetection,
    manifestServiceFlags: ManifestServiceFlags,
    manifestServiceStats: ManifestServiceStats,
  ) {
    super(
      node,
      api,
      editorPointerAPI,
      manifestChangeDetection,
      manifestServiceFlags,
      manifestServiceStats,
    );

    this.#displayFilterResolver = new ManifestDisplayFilterResolver(
      this.node,
      this.api,
      this.editorPointerAPI,
      this.manifestChangeDetection,
      this.manifestServiceFlags,
      this.manifestServiceStats,
    );
  }

  #getFilteredDefinitions<TKey extends ReducibleKey>(
    manifestEntityKey: TKey,
  ): NonNullable<EditorElement[TKey]> {
    const definitions = this.#getDefinitions(manifestEntityKey);
    const editableItems =
      this.#displayFilterResolver.getEditableItems()[manifestEntityKey];

    return Object.fromEntries(
      Object.entries(definitions).filter(([key]) => {
        return editableItems.has(key);
      }),
    );
  }

  #getDefinitions<TKey extends ReducibleKey>(
    manifestEntityKey: TKey,
  ): NonNullable<EditorElement[TKey]> {
    return (
      this.virtualManifest.tree.manifest?.editorElement?.[manifestEntityKey] ??
      ({} as NonNullable<EditorElement[TKey]>)
    );
  }

  getStyleDefinitions(): {
    cssProperties: NonNullable<EditorElement['cssProperties']>;
    cssCustomProperties: NonNullable<EditorElement['cssCustomProperties']>;
  } {
    return {
      cssProperties: this.#getFilteredDefinitions('cssProperties'),
      cssCustomProperties: this.#getFilteredDefinitions('cssCustomProperties'),
    };
  }

  getDataDefinitions(): NonNullable<EditorElement['data']> {
    return this.#getFilteredDefinitions('data');
  }

  getCustomActionDefinitions(): NonNullable<EditorElement['customActions']> {
    return this.#getFilteredDefinitions('customActions');
  }

  getActionDefinitions(): NonNullable<EditorElement['actions']> {
    return this.#getFilteredDefinitions('actions');
  }

  #getAllStateDefinitions(): NonNullable<EditorElement['states']> {
    const filteredDefinitions = this.#getFilteredDefinitions('states');

    if (Object.keys(filteredDefinitions).length) {
      return filteredDefinitions;
    }

    const viewStates = this.virtualManifest.manifests.map(
      ({ editorPointer, store }) => {
        return this.api.state.getViewStates(editorPointer, store.extensionType);
      },
    );

    return viewStates.reduce((acc, x) => ({ ...acc, ...x }), {});
  }

  getStateDefinitions(): NonNullable<EditorElement['states']> {
    const allStates = this.#getAllStateDefinitions();

    return Object.fromEntries(
      Object.entries(allStates).filter(([, state]) => !isDesignState(state)),
    );
  }

  getDesignStateDefinitions(): NonNullable<EditorElement['states']> {
    const allStates = this.#getAllStateDefinitions();

    return Object.fromEntries(
      Object.entries(allStates).filter(([, state]) => isDesignState(state)),
    );
  }

  #getPresetDefinitions({
    shouldApplyDisplayFilters = true,
  }: {
    shouldApplyDisplayFilters?: boolean;
  } = {}): NonNullable<EditorElement['presets']> {
    if (this.node.isMultipleComponents()) {
      return {};
    }

    const editorPointer = this.node.getEditorPointer();

    if (!editorPointer) {
      return {};
    }

    /**
     * why do we fetch breakpoint by editor pointer?
     * it seems like it should not depend on an editor pointer
     */
    const currentBreakpoint =
      this.api.stage.getBreakpointFromPointer(editorPointer);

    const presets = shouldApplyDisplayFilters
      ? this.#getFilteredDefinitions('presets')
      : this.#getDefinitions('presets');

    if (Object.keys(presets).length === 0) {
      return presets;
    }

    return Object.fromEntries(
      Object.entries(presets).filter(([_presetKey, presetDefinition]) => {
        if (!presetDefinition) {
          return false;
        }

        return (
          !presetDefinition.optimizedFor ||
          presetDefinition.optimizedFor.length === 0 ||
          presetDefinition.optimizedFor.includes(currentBreakpoint)
        );
      }),
    );
  }

  getPresetDefinitions(): NonNullable<EditorElement['presets']> {
    return this.#getPresetDefinitions();
  }

  getLayoutDefinitions(): NonNullable<EditorElement['layout']> {
    /**
     * TODO: virtual manifest has merged layout,
     * so probably we may allow this function to work for multiple pointers
     */
    if (this.node.isMultipleComponents()) {
      return {};
    }

    const editorPointer = this.node.getEditorPointer();

    const definitions =
      this.virtualManifest.tree.manifest.editorElement?.layout || {};

    const presets = this.#getPresetDefinitions({
      shouldApplyDisplayFilters: false,
    });

    const appliedPreset = this.node
      .extract(editorPointer)
      .getAPI(VirtualElementAPIType.ManifestQueryReadonly)
      .getAppliedPreset();

    if (!appliedPreset) {
      return definitions;
    }

    return presets?.[appliedPreset]?.presetDefaults?.layout || definitions;
  }

  getDisplayGroupDefinitions(options?: {
    filter?: { groupType?: DisplayGroups[string]['groupType'] };
  }): NonNullable<EditorElement['displayGroups']> {
    const displayGroups =
      this.virtualManifest.tree.manifest.editorElement?.displayGroups;

    if (!displayGroups) {
      return {};
    }

    const groupTypeFilter = options?.filter?.groupType;

    if (!groupTypeFilter) {
      return displayGroups;
    }

    return Object.fromEntries(
      Object.entries(displayGroups).filter(
        ([_groupKey, displayGroup]) =>
          displayGroup.groupType === groupTypeFilter,
      ),
    );
  }
}
